import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X, CalendarDays } from "lucide-react";

const LOGO = "/fabg-logo.png";

const CANADA_NAV = [
  { id: "why", label: "Why FABG" },
  { id: "benefits", label: "Benefits" },
  { id: "pricing", label: "Pricing" },
  { id: "testimonials", label: "Testimonials" },
  { id: "faq", label: "FAQ" },
];

export const Header = ({
  onApply,
  navItems = CANADA_NAV,
  ctaLabel = "Apply for Founding Membership",
  ctaShort = "Apply",
  secondaryCta = null, // { label, href }
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const handleApply = () => {
    setMobileOpen(false);
    onApply?.();
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b border-border bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 transition-shadow duration-200 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div
        className={`max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4 transition-all duration-200 ${
          scrolled ? "h-14" : "h-16"
        }`}
      >
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex flex-none items-center"
          aria-label="FABG home"
        >
          <img
            src={LOGO}
            alt="FABG — The Full Arch Buying Group"
            className="h-8 sm:h-9 w-auto"
            data-testid="header-logo"
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goTo(item.id)}
              data-testid={`nav-${item.id}`}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-foreground hover:bg-secondary transition-colors duration-200"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {secondaryCta ? (
            <a
              href={secondaryCta.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-secondary-cta"
              className="hidden sm:inline-flex h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm font-medium text-slate-700 hover:bg-secondary transition-colors duration-200"
            >
              <CalendarDays className="h-4 w-4" />
              {secondaryCta.label}
            </a>
          ) : null}

          <Button
            onClick={handleApply}
            data-testid="header-apply-button"
            className="h-10 px-4 rounded-xl font-semibold shadow-sm hover:-translate-y-px hover:shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            <span className="hidden sm:inline">{ctaLabel}</span>
            <span className="sm:hidden">{ctaShort}</span>
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-slate-700 hover:bg-secondary transition-colors duration-200"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav panel */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-border bg-white"
          data-testid="mobile-menu-panel"
        >
          <nav
            className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col"
            aria-label="Mobile"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(item.id)}
                data-testid={`mobile-nav-${item.id}`}
                className="w-full text-left rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-secondary transition-colors duration-200"
              >
                {item.label}
              </button>
            ))}
            {secondaryCta ? (
              <a
                href={secondaryCta.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-secondary-cta"
                className="mt-1 inline-flex items-center gap-2 rounded-lg px-3 py-3 text-base font-medium text-primary"
              >
                <CalendarDays className="h-4 w-4" />
                {secondaryCta.label}
              </a>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
};
